import React from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'components/ui/Modal'
import { Button } from 'components/ui/Button'
import { AlertTriangle, Shield, Users } from 'lucide-react'
import { type Role } from 'lib/api'

interface DeleteRoleModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  role: Role | null
  loading?: boolean
}

export function DeleteRoleModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  role, 
  loading = false 
}: DeleteRoleModalProps) {
  const { t } = useTranslation()

  if (!role) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('roles.deleteRole')}
      className="max-w-md"
    >
      <div className="space-y-4">
        {/* Warning Icon */}
        <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 dark:bg-red-900/20 rounded-full">
          <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
        </div>

        {/* Warning Message */}
        <div className="text-center">
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {t('roles.messages.confirmDelete')}
          </h3>
          <p className="text-muted-foreground">
            {t('roles.messages.deleteWarning')}
          </p>
        </div>

        {/* Role Info */}
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div 
              className="h-10 w-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: role.color }}
            >
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-foreground">
                {role.name}
              </p>
              <p className="text-sm text-muted-foreground">
                {role.description}
              </p>
              <div className="flex items-center space-x-4 mt-2">
                <div className="flex items-center space-x-1">
                  <Users className="h-3 w-3 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">
                    {t('roles.permissions')}: {role.permission_count}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Warning Box */}
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            {t('roles.messages.deleteWarningDetails')}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            loading={loading}
          >
            {t('roles.deleteRole')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
